import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  ArrowLeft,
  Box,
  DollarSign,
  Download,
  ExternalLink,
  FileCheck,
  Loader2,
  MapPin,
  Package,
  Printer,
  RefreshCw,
  Weight,
} from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { api } from '@/api/client';
import BatchProgressPanel from '@/components/upload/BatchProgressPanel';
import PDFViewerDialog from '@/components/mail/PDFViewerDialog';

export default function OrderDetailsPage() {
  const { id } = useParams();
  const queryClient = useQueryClient();
  const [viewing, setViewing] = useState(null);
  const [refundingId, setRefundingId] = useState(null);

  const { data: order, isLoading } = useQuery({
    queryKey: ['order', id],
    queryFn: () => api.request(`/orders/${id}`),
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['order', id] });
    queryClient.invalidateQueries({ queryKey: ['orders'] });
  };

  const refundMutation = useMutation({
    mutationFn: (parcel) =>
      api.request(`/orders/${id}/parcels/${parcel.id}/refund`, { method: 'POST' }),
    onSuccess: (data) => {
      invalidate();
      if (data.refunded) {
        toast.success('Parcel refunded and removed from the order');
      } else {
        toast.warning(`Removed, but the refund did not go through: ${data.error ?? data.status}`);
      }
    },
    onError: (error) => toast.error(error.message ?? 'Could not refund that parcel'),
    onSettled: () => setRefundingId(null),
  });

  const retryMutation = useMutation({
    mutationFn: (runId) => api.request(`/batches/${runId}/retry-failed`, { method: 'POST' }),
    onSuccess: (data) => {
      invalidate();
      toast.success(`Retrying ${data.retrying} row(s)`);
    },
    onError: (error) => toast.error(error.message ?? 'Could not retry'),
  });

  const manifestMutation = useMutation({
    mutationFn: () => api.request(`/orders/${id}/manifest`, { method: 'POST' }),
    onSuccess: () => {
      invalidate();
      toast.success('Manifest created');
    },
    onError: (error) => toast.error(error.message ?? 'Could not create the manifest'),
  });

  if (isLoading) {
    return (
      <div className="flex justify-center py-24">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  if (!order) {
    return (
      <div className="p-8 max-w-3xl mx-auto">
        <Card className="border-none shadow-lg">
          <CardContent className="p-12 text-center">
            <Package className="w-16 h-16 mx-auto mb-4 text-slate-300" />
            <h3 className="text-lg font-semibold text-slate-700 mb-2">Order not found</h3>
            <Link to="/shipping" className="text-blue-600 hover:underline">
              Back to orders
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  const run = order.batchRuns?.[0];
  const inProgress = run && ['VALIDATING', 'PURCHASING', 'READY'].includes(run.status);
  const failedRows = (run?.items ?? []).filter((item) => item.status === 'FAILED');
  const skippedRows = (run?.items ?? []).filter((item) => item.status === 'SKIPPED');

  return (
    <div className="p-6 md:p-8 max-w-7xl mx-auto space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <Button asChild variant="outline" size="icon">
            <Link to="/shipping" aria-label="Back to orders">
              <ArrowLeft className="w-4 h-4" />
            </Link>
          </Button>
          <div>
            <h1 className="text-3xl font-bold text-slate-900">{order.orderName}</h1>
            <p className="text-slate-600">Order {order.orderNumber}</p>
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          {order.hasLabels && (
            <Button
              onClick={() =>
                setViewing({
                  url: `/api/documents/orders/${order.id}/labels`,
                  title: `${order.orderName} — all labels`,
                  // The merged PDF is for the browser; the agent prints the
                  // parcels' own labels, in the order they are listed here.
                  parcelIds: order.parcels.map((parcel) => parcel.id),
                })
              }
              className="bg-blue-600 hover:bg-blue-700 gap-2"
            >
              <Printer className="w-4 h-4" />
              Print all labels
            </Button>
          )}

          {order.hasManifest ? (
            <Button
              variant="outline"
              className="gap-2"
              onClick={() =>
                setViewing({
                  url: `/api/documents/orders/${order.id}/manifest`,
                  title: `${order.orderName} — manifest`,
                })
              }
            >
              <FileCheck className="w-4 h-4" />
              Manifest
            </Button>
          ) : (
            <Button
              variant="outline"
              className="gap-2"
              onClick={() => manifestMutation.mutate()}
              disabled={manifestMutation.isPending}
            >
              {manifestMutation.isPending ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <FileCheck className="w-4 h-4" />
              )}
              Create manifest
            </Button>
          )}

          <Button variant="outline" asChild className="gap-2">
            <a href={`/api/orders/${order.id}/export`} download>
              <Download className="w-4 h-4" />
              Export CSV
            </a>
          </Button>
        </div>
      </div>

      {inProgress && (
        <BatchProgressPanel batchId={run.id} orderId={order.id} onStartNew={() => {}} />
      )}

      <Card className="border-none shadow-lg">
        <CardHeader className="border-b bg-gradient-to-r from-blue-50 to-indigo-50">
          <CardTitle>Summary</CardTitle>
        </CardHeader>
        <CardContent className="p-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <Summary label="Parcels" value={order.parcels.length} />
            <Summary label="Status" value={order.status.toLowerCase()} capitalize />
            <Summary label="Total cost" value={`$${Number(order.totalCost).toFixed(2)}`} />
            <Summary label="Created" value={format(new Date(order.createdAt), 'MMM d, yyyy')} />
          </div>

          {order.createdBy && (
            <p className="text-sm text-slate-500 mt-4">Started by {order.createdBy.name}</p>
          )}
        </CardContent>
      </Card>

      {failedRows.length > 0 && (
        <Alert className="border-red-200 bg-red-50">
          <AlertTriangle className="h-4 w-4 text-red-600" />
          <AlertDescription className="text-red-800">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <span>
                <strong>
                  {failedRows.length} row{failedRows.length === 1 ? '' : 's'} did not ship.
                </strong>{' '}
                Nothing was charged for those.
              </span>
              <Button
                size="sm"
                variant="outline"
                onClick={() => retryMutation.mutate(run.id)}
                disabled={retryMutation.isPending}
                className="border-red-300 text-red-700 hover:bg-red-100 gap-1"
              >
                <RefreshCw className={`w-4 h-4 ${retryMutation.isPending ? 'animate-spin' : ''}`} />
                Retry them
              </Button>
            </div>

            <ul className="mt-3 space-y-1 text-sm">
              {failedRows.slice(0, 10).map((item) => (
                <li key={item.id}>
                  Row {item.rowIndex + 1} — {item.recipientName}: {item.lastError}
                </li>
              ))}
              {failedRows.length > 10 && <li>…and {failedRows.length - 10} more</li>}
            </ul>
          </AlertDescription>
        </Alert>
      )}

      {skippedRows.length > 0 && (
        <Alert className="border-slate-200 bg-slate-50">
          <AlertDescription className="text-slate-700">
            {skippedRows.length} row{skippedRows.length === 1 ? ' was' : 's were'} skipped and never
            shipped.
          </AlertDescription>
        </Alert>
      )}

      <div className="space-y-4">
        <h2 className="text-xl font-semibold text-slate-900">Parcels ({order.parcels.length})</h2>

        {order.parcels.length === 0 ? (
          <Card className="border-none shadow-lg">
            <CardContent className="p-10 text-center text-slate-500">
              No parcels have shipped in this order yet.
            </CardContent>
          </Card>
        ) : (
          <ul className="space-y-4">
            {order.parcels.map((parcel, index) => (
              <li key={parcel.id}>
                <Card
                  className={`border-none shadow-lg ${
                    parcel.status === 'REFUNDED' && !parcel.refundError ? 'opacity-60' : ''
                  }`}
                >
                  <CardContent className="p-5">
                    <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
                      <div className="flex items-center gap-3">
                        <div className="w-11 h-11 bg-blue-100 rounded-lg flex items-center justify-center shrink-0">
                          <Package className="w-5 h-5 text-blue-600" />
                        </div>
                        <div>
                          <h3 className="font-semibold">
                            Parcel {index + 1}
                            {parcel.status === 'REFUNDED' &&
                              (parcel.refundError ? (
                                // The carrier would not take it back, so the
                                // cost stands. Saying "refunded" here would be
                                // the opposite of what happened.
                                <span
                                  className="ml-2 rounded bg-red-100 px-1.5 py-0.5 text-xs font-medium text-red-800"
                                  title={parcel.refundError}
                                >
                                  refund refused
                                </span>
                              ) : (
                                <span className="ml-2 text-xs font-normal text-slate-500">
                                  refunded
                                </span>
                              ))}
                          </h3>
                          <p className="text-sm text-slate-500">
                            {parcel.trackingUrl ? (
                              <a
                                href={parcel.trackingUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="font-mono text-blue-600 hover:underline"
                              >
                                {parcel.trackingNumber}
                              </a>
                            ) : (
                              <span className="font-mono">{parcel.trackingNumber ?? '—'}</span>
                            )}
                          </p>
                          <p
                            className={`flex items-center gap-1 text-sm font-semibold mt-0.5 ${
                              parcel.status === 'REFUNDED' && !parcel.refundError
                                ? 'text-slate-400 line-through'
                                : 'text-green-700'
                            }`}
                          >
                            <DollarSign className="w-3.5 h-3.5" />
                            {Number(parcel.shippingCost).toFixed(2)}
                          </p>
                        </div>
                      </div>

                      {parcel.status !== 'REFUNDED' && (
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            className="gap-1"
                            onClick={() =>
                              setViewing({
                                url: `/api/documents/parcels/${parcel.id}/label`,
                                title: `Label — ${parcel.recipientName}`,
                                parcelIds: [parcel.id],
                              })
                            }
                          >
                            <ExternalLink className="w-4 h-4" />
                            View
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            className="border-red-300 text-red-700 hover:bg-red-50 gap-1"
                            disabled={refundingId === parcel.id}
                            onClick={() => {
                              if (
                                window.confirm(
                                  `Refund the label for ${parcel.recipientName} ($${Number(parcel.shippingCost).toFixed(2)})?`,
                                )
                              ) {
                                setRefundingId(parcel.id);
                                refundMutation.mutate(parcel);
                              }
                            }}
                          >
                            <RefreshCw
                              className={`w-4 h-4 ${refundingId === parcel.id ? 'animate-spin' : ''}`}
                            />
                            Refund
                          </Button>
                        </div>
                      )}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                      <div>
                        <p className="flex items-center gap-1.5 text-slate-500 mb-1">
                          <MapPin className="w-4 h-4" />
                          Recipient
                        </p>
                        <p className="font-medium">{parcel.recipientName}</p>
                        <p className="text-slate-600">{parcel.recipientAddress1}</p>
                        {parcel.recipientAddress2 && (
                          <p className="text-slate-600">{parcel.recipientAddress2}</p>
                        )}
                        <p className="text-slate-600">
                          {parcel.recipientCity}, {parcel.recipientState} {parcel.recipientZip}
                        </p>
                      </div>

                      <div>
                        <p className="flex items-center gap-1.5 text-slate-500 mb-1">
                          <Box className="w-4 h-4" />
                          Box
                        </p>
                        <p className="font-medium">
                          {parcel.boxLength}&quot; × {parcel.boxWidth}&quot; × {parcel.boxHeight}
                          &quot;
                        </p>
                        {parcel.serviceName && (
                          <p className="text-slate-600">
                            {parcel.carrier?.toUpperCase()} {parcel.serviceName}
                          </p>
                        )}
                      </div>

                      <div>
                        <p className="flex items-center gap-1.5 text-slate-500 mb-1">
                          <Weight className="w-4 h-4" />
                          Weight
                        </p>
                        <p className="font-medium">{parcel.weight} lbs</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </li>
            ))}
          </ul>
        )}
      </div>

      {viewing && (
        <PDFViewerDialog
          open
          onClose={() => setViewing(null)}
          documentUrl={viewing.url}
          title={viewing.title}
          parcelIds={viewing.parcelIds ?? []}
        />
      )}
    </div>
  );
}

function Summary({ label, value, capitalize }) {
  return (
    <div>
      <p className="text-sm text-slate-500 mb-1">{label}</p>
      <p className={`text-2xl font-bold text-slate-900 ${capitalize ? 'capitalize' : ''}`}>
        {value}
      </p>
    </div>
  );
}
